import { motion } from "framer-motion";

// Color / short-label map for common song sections
const SECTION_STYLES = {
  intro:      { bg: "bg-emerald-500/15",     border: "border-emerald-500/40",     text: "text-emerald-400",     short: "IN" },
  outro:      { bg: "bg-emerald-500/15",     border: "border-emerald-500/40",     text: "text-emerald-400",     short: "OUT" },
  verse:      { bg: "bg-zinc-700/40",     border: "border-zinc-500/40",     text: "text-zinc-200",     short: "V" },
  "pre-chorus":{ bg: "bg-fuchsia-500/15", border: "border-fuchsia-400/40",  text: "text-fuchsia-200",  short: "PC" },
  prechorus:  { bg: "bg-fuchsia-500/15",  border: "border-fuchsia-400/40",  text: "text-fuchsia-200",  short: "PC" },
  postchorus: { bg: "bg-fuchsia-500/15",  border: "border-fuchsia-400/40",  text: "text-fuchsia-200",  short: "POC" },
  chorus:     { bg: "bg-fuchsia-500/25",  border: "border-fuchsia-400/60",  text: "text-fuchsia-100",  short: "CH" },
  hook:       { bg: "bg-fuchsia-500/25",  border: "border-fuchsia-400/60",  text: "text-fuchsia-100",  short: "HK" },
  refrain:    { bg: "bg-fuchsia-500/25",  border: "border-fuchsia-400/60",  text: "text-fuchsia-100",  short: "RF" },
  bridge:     { bg: "bg-yellow-400/15",   border: "border-yellow-400/40",   text: "text-yellow-200",   short: "BR" },
  drop:       { bg: "bg-yellow-400/25",   border: "border-yellow-400/60",   text: "text-yellow-100",   short: "DR" },
  breakdown:  { bg: "bg-yellow-400/15",   border: "border-yellow-400/40",   text: "text-yellow-200",   short: "BD" },
  solo:       { bg: "bg-orange-500/15",   border: "border-orange-400/40",   text: "text-orange-200",   short: "SL" },
  instrumental:{ bg: "bg-orange-500/15",  border: "border-orange-400/40",   text: "text-orange-200",   short: "IN" },
  interlude:  { bg: "bg-orange-500/15",   border: "border-orange-400/40",   text: "text-orange-200",   short: "IT" },
  coda:       { bg: "bg-emerald-500/15",     border: "border-emerald-500/40",     text: "text-emerald-400",     short: "CO" },
};

const DEFAULT_STYLE = { bg: "bg-zinc-800/60", border: "border-zinc-600/40", text: "text-zinc-300", short: "?" };

const parseStructure = (raw) => {
  if (!raw) return [];
  const parts = raw
    .replace(/\b(pre|post)[\s-]+(chorus)/gi, "$1chorus")
    .split(/\s*(?:→|->|\||\/|,|;|\s-\s|-)\s*/)
    .map((p) => p.trim())
    .filter(Boolean);
  const blocks = parts.map((p, i) => {
    const lower = p.toLowerCase();
    const key = Object.keys(SECTION_STYLES).find((k) => lower.startsWith(k));
    const style = key ? SECTION_STYLES[key] : DEFAULT_STYLE;
    const numMatch = lower.match(/(\d+)/);
    const short = key ? style.short + (numMatch ? numMatch[1] : "") : p.slice(0, 4).toUpperCase();
    return { id: i, label: p.replace(/\s+/g, " "), short, style, known: !!key };
  });
  // Free-text structure with no section names -> one full-track segment
  if (!blocks.some((b) => b.known)) return [{ id: 0, label: raw, short: "FULL TRACK", style: DEFAULT_STYLE }];
  return blocks;
};

export const StructureTimeline = ({ structure }) => {
  const blocks = parseStructure(structure);
  if (!blocks.length) return null;

  return (
    <div className="border border-white/10 bg-zinc-900/40 rounded-sm p-5" data-testid="structure-timeline">
      <div className="flex items-center justify-between mb-4">
        <span className="font-mono-display text-[10px] tracking-[0.3em] uppercase text-zinc-500">
          arrangement // timeline
        </span>
        <span className="font-mono-display text-[10px] tracking-[0.2em] uppercase text-emerald-500">
          {blocks.length} sections
        </span>
      </div>

      <div className="flex items-stretch gap-1 w-full overflow-x-auto pb-2">
        {blocks.map((b, i) => (
          <motion.div
            key={b.id}
            initial={{ opacity: 0, scaleY: 0.3 }}
            animate={{ opacity: 1, scaleY: 1 }}
            transition={{ duration: 0.3, delay: i * 0.05 }}
            className={`relative group flex-1 min-w-[52px] h-16 border ${b.style.bg} ${b.style.border} flex items-center justify-center rounded-sm hover:brightness-125 transition-all cursor-default`}
            data-testid={`structure-block-${i}`}
            title={b.label}
          >
            <span className={`font-mono-display text-[10px] tracking-widest font-bold ${b.style.text}`}>
              {b.short}
            </span>
            <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap font-mono-display text-[9px] uppercase tracking-[0.15em] text-zinc-500 opacity-0 group-hover:opacity-100 transition-opacity">
              {b.label}
            </span>
          </motion.div>
        ))}
      </div>

      {/* Legend */}
      <div className="mt-8 flex flex-wrap gap-x-4 gap-y-2 font-mono-display text-[10px] tracking-[0.15em] uppercase text-zinc-500">
        {[
          ["intro / outro", "bg-emerald-500/50"],
          ["verse", "bg-zinc-500/60"],
          ["pre-chorus", "bg-fuchsia-400/40"],
          ["chorus / hook", "bg-fuchsia-400/70"],
          ["bridge / drop", "bg-yellow-400/60"],
          ["solo / instrumental", "bg-orange-400/50"],
        ].map(([lbl, cls]) => (
          <div key={lbl} className="flex items-center gap-1.5">
            <span className={`inline-block w-3 h-3 rounded-sm ${cls}`} />
            <span>{lbl}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
